import { supabase } from "./supabase";
import { sha256 } from "./utils/sha256";
const fetchCategoryCount = async (userEmail) => {
    try {
        // Fetch the user data based on the entered email
        const { data, error: fetchError } = await supabase
            .from('user')
            .select('category_count')
            .eq('email', userEmail)
            .single(); // Expecting a single result

        if (fetchError) {
            throw fetchError;
        }

        if (data) {
            return data.category_count;
        } else {
            return null;
        }
    } catch (error) {
        //console.log('Error', error.message);
    }
}

const retrieveUserEmail = async (values) => {
    try {
        const { data: user, error: fetchError } = await supabase
        .from('user')
        .select('email')
        .eq('id', values.accountId)
        .single()

        if (fetchError) {
            console.error('Error fetching email:', fetchError);
            return null;
        }

        if (user) {
            return user.email;
        } else {
            return null;
        }

    } catch (error) {
        console.log('Error:', error);
    }
};

const fetchBusinessInfo = async (userEmail) => {
    try {
        const { data: info, error } = await supabase
            .from('user')
            .select('business_logo, business_name, business_address, business_phone, business_whatsapp, country_code, id, website, business_currency')
            .eq('email', userEmail)
            .single()

        if(info) {
            return info;
        }

    } catch (error) {
        return null;
    }
};

const fetchCategoryName = async (values) => {
    try {
        const { data: info, error } = await supabase
            .from('user_categories')
            .select('category_name')
            .eq('email', values.userEmail)
            .eq('category_id', values.categoryId)
            .single()

        if(info) {
            return info.category_name;
        }
    } catch (error) {
        console.log('Error:', error);
    }
};

const fetchProductInfo = async (values) => {
    try {
        const { data: info, error } = await supabase
            .from('user_products')
            .select('product_name, product_price, product_description')
            .eq('email', values.userEmail)
            .eq('category_id', values.categoryId)
            .eq('image_index', values.imageIndex)
            .single();

        if (error) {
            console.error('Fetch error:', error);
            return null;
        }

        return info;
    } catch (error) {
        console.error('Unexpected error:', error);
        return null;
    }
};

const fetchBusinessType = async (userEmail) => {
    try {
        const { data: info, error } = await supabase
            .from('user')
            .select('business_type')
            .eq('email', userEmail)
            .single()

        if(info) {
            return info;
        }

    } catch (error) {
        return null;
    }
};

const fetchBusinessPresentation = async (userEmail) => {
    try {
        const { data: info, error } = await supabase
            .from('user')
            .select('about_section, services_section')
            .eq('email', userEmail)
            .single()

        if(info) {
            return info;
        }

    } catch (error) {
        return null;
    }
};

const fetchProductOffer = async (values) => {
    try {
        const { data: info, error } = await supabase
            .from('user_offers')
            .select('product_name, product_price, previous_product_price, product_description')
            .eq('email', values.userEmail)
            .eq('category_id', values.categoryId)
            .eq('image_index', values.imageIndex)
            .maybeSingle();

        if (error) {
            console.error('Fetch error:', error);
            return null;
        }

        return info;
    } catch (error) {
        console.error('Unexpected error:', error);
        return null;
    }
};

const fetchItemInfo = async (values) => {
    try {
        const { data: info, error } = await supabase
            .from('user_portfolio')
            .select('item_name, item_description')
            .eq('email', values.userEmail)
            .eq('category_id', values.categoryId)
            .eq('image_index', values.imageIndex)
            .maybeSingle();

        if (error) {
            console.error('Fetch error:', error);
            return null;
        }

        return info;
    } catch (error) {
        console.error('Unexpected error:', error);
        return null;
    }
};

const fetchCategoryIds = async (userEmail) => {
    try {
        const { data, error: fetchError } = await supabase
            .from('user_categories')
            .select('category_id')
            .eq('email', userEmail)

        if (fetchError) {
            throw fetchError;
        } else {
            const categoryIds = data.map(item => item);

            return categoryIds;
        }
    } catch (error) {
        return;
    }
}

const checkAccount = async (email) => {
    const { data } = await supabase
        .from('user')
        .select('email')
        .eq('email', email)
        .maybeSingle();
    return !!data;
};

const loginUser = async (email, password) => {
    const hash = await sha256(password);
    const { data, error } = await supabase
        .from('user')
        .select('email, business_type, business_currency')
        .eq('email', email)
        .eq('password', hash)
        .maybeSingle();
    if (error || !data) return null;
    return data;
};

const registerUser = async (email, password) => {
    const hash = await sha256(password);
    const { error } = await supabase
        .from('user')
        .insert({
            email,
            password: hash,
            category_count: 0,
            country_code: '+1',
            subscribed: false,
        });
    return !error;
};

const saveSecurityQuestions = async (email, desiredName, favoriteDish) => {
    const { error } = await supabase
        .from('user')
        .update({ desired_name: desiredName, favorite_dish: favoriteDish })
        .eq('email', email);
    return !error;
};

const verifySecurityQuestions = async (email, desiredName, favoriteDish) => {
    const { data } = await supabase
        .from('user')
        .select('desired_name, favorite_dish')
        .eq('email', email)
        .maybeSingle();
    if (!data) return false;
    return (
        data.desired_name === desiredName &&
        data.favorite_dish === favoriteDish
    );
};

const updateBusinessTypeAndCurrency = async (email, businessType, currency) => {
    const { error } = await supabase
        .from('user')
        .update({ business_type: businessType, business_currency: currency })
        .eq('email', email);
    return !error;
};

const resetPassword = async (email, newPassword) => {
    const hash = await sha256(newPassword);
    const { error } = await supabase
        .from('user')
        .update({ password: hash })
        .eq('email', email);
    return !error;
};

const fetchSubscriptionStatus = async (email) => {
    try {
        const { data } = await supabase
            .from('user')
            .select('subscribed')
            .eq('email', email)
            .single();
        return data?.subscribed === true;
    } catch (_) {
        return false;
    }
};

// ─── Phase 2: Write / upload / delete functions ──────────────────────────────

const updateBusinessInfo = async (email, data) => {
    const { error } = await supabase
        .from('user')
        .update({
            business_name: data.businessName,
            business_address: data.businessAddress,
            business_phone: data.businessPhone,
            business_whatsapp: data.businessWhatsapp,
            country_code: data.countryCode,
            website: data.website,
        })
        .eq('email', email);
    return !error;
};

const updateBusinessPresentation = async (email, aboutSection, servicesSection) => {
    const { error } = await supabase
        .from('user')
        .update({ about_section: aboutSection, services_section: servicesSection })
        .eq('email', email);
    return !error;
};

const uploadAndSetLogo = async (email, file) => {
    const ext = file.name.split('.').pop() || 'jpg';
    const filename = `logo_${Date.now()}.${ext}`;
    const path = `${email}/Logo/${filename}`;
    const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(path, file, { upsert: true });
    if (uploadError) return null;
    const { data: { publicUrl } } = supabase.storage.from('images').getPublicUrl(path);
    await supabase.from('user').update({ business_logo: publicUrl }).eq('email', email);
    return publicUrl;
};

const listFolderImages = async (email, folderId) => {
    const { data, error } = await supabase.storage
        .from('images')
        .list(`${email}/${folderId}`);
    if (error || !data) return [];
    return data
        .filter(f => f.name !== '.emptyFolderPlaceholder')
        .map(file => ({
            name: file.name,
            url: supabase.storage.from('images').getPublicUrl(`${email}/${folderId}/${file.name}`).data.publicUrl,
        }));
};

const uploadItemImage = async (email, folderId, file) => {
    const ext = file.name.split('.').pop() || 'jpg';
    const filename = `${Date.now()}.${ext}`;
    const { error } = await supabase.storage
        .from('images')
        .upload(`${email}/${folderId}/${filename}`, file);
    if (error) return null;
    return filename;
};

const addProduct = async (email, categoryId, imageIndex, name, price, description) => {
    const { error } = await supabase.from('user_products').insert({
        email, category_id: categoryId, image_index: imageIndex,
        product_name: name, product_price: price, product_description: description,
    });
    return !error;
};

const updateProduct = async (email, categoryId, imageIndex, name, price, description) => {
    const { error } = await supabase
        .from('user_products')
        .update({ product_name: name, product_price: price, product_description: description })
        .eq('email', email)
        .eq('category_id', categoryId)
        .eq('image_index', imageIndex);
    return !error;
};

const deleteProducts = async (email, categoryId, imageIndexes) => {
    const paths = imageIndexes.map(idx => `${email}/${categoryId}/${idx}`);
    await supabase.storage.from('images').remove(paths);
    const { error } = await supabase
        .from('user_products')
        .delete()
        .eq('email', email)
        .eq('category_id', categoryId)
        .in('image_index', imageIndexes);
    return !error;
};

const addCategory = async (email, categoryId, categoryName) => {
    const { error } = await supabase.from('user_categories').insert({
        email, category_id: categoryId, category_name: categoryName,
    });
    return !error;
};

const renameCategory = async (email, categoryId, name) => {
    const { error } = await supabase
        .from('user_categories')
        .update({ category_name: name })
        .eq('email', email)
        .eq('category_id', categoryId);
    return !error;
};

const deleteCategoryAndContents = async (email, categoryId) => {
    const { data: files } = await supabase.storage
        .from('images')
        .list(`${email}/${categoryId}`);
    if (files && files.length > 0) {
        const paths = files.map(f => `${email}/${categoryId}/${f.name}`);
        await supabase.storage.from('images').remove(paths);
    }
    await supabase.from('user_products').delete().eq('email', email).eq('category_id', categoryId);
    await supabase.from('user_portfolio').delete().eq('email', email).eq('category_id', categoryId);
    const { error } = await supabase
        .from('user_categories')
        .delete()
        .eq('email', email)
        .eq('category_id', categoryId);
    return !error;
};

const updateCategoryCount = async (email, count) => {
    await supabase.from('user').update({ category_count: count }).eq('email', email);
};

// Store: Offers (uploaderId = 1)
const addOffer = async (email, imageIndex, name, price, previousPrice, description) => {
    const { error } = await supabase.from('user_offers').insert({
        email, category_id: 1, image_index: imageIndex,
        product_name: name, product_price: price,
        previous_product_price: previousPrice, product_description: description,
    });
    return !error;
};

const updateOffer = async (email, imageIndex, { name, price, previousPrice, description }) => {
    const { error } = await supabase
        .from('user_offers')
        .update({
            product_name: name, product_price: price,
            previous_product_price: previousPrice, product_description: description,
        })
        .eq('email', email)
        .eq('image_index', imageIndex);
    return !error;
};

const deleteOffers = async (email, imageIndexes) => {
    const paths = imageIndexes.map(idx => `${email}/1/${idx}`);
    await supabase.storage.from('images').remove(paths);
    const { error } = await supabase
        .from('user_offers')
        .delete()
        .eq('email', email)
        .in('image_index', imageIndexes);
    return !error;
};

// Store: New Products (uploaderId = 2)
const addStoreProduct = async (email, imageIndex, name, price, description) => {
    const { error } = await supabase.from('user_products').insert({
        email, category_id: 2, image_index: imageIndex,
        product_name: name, product_price: price, product_description: description,
    });
    return !error;
};

const updateStoreProduct = async (email, imageIndex, name, price, description) => {
    const { error } = await supabase
        .from('user_products')
        .update({ product_name: name, product_price: price, product_description: description })
        .eq('email', email)
        .eq('category_id', 2)
        .eq('image_index', imageIndex);
    return !error;
};

const deleteStoreProducts = async (email, imageIndexes) => {
    const paths = imageIndexes.map(idx => `${email}/2/${idx}`);
    await supabase.storage.from('images').remove(paths);
    const { error } = await supabase
        .from('user_products')
        .delete()
        .eq('email', email)
        .eq('category_id', 2)
        .in('image_index', imageIndexes);
    return !error;
};

// Portfolio items (service providers)
const addPortfolioItem = async (email, categoryId, imageIndex, name, description) => {
    const { error } = await supabase.from('user_portfolio').insert({
        email, category_id: categoryId, image_index: imageIndex,
        item_name: name, item_description: description,
    });
    return !error;
};

const updatePortfolioItem = async (email, categoryId, imageIndex, name, description) => {
    const { error } = await supabase
        .from('user_portfolio')
        .update({ item_name: name, item_description: description })
        .eq('email', email)
        .eq('category_id', categoryId)
        .eq('image_index', imageIndex);
    return !error;
};

const deletePortfolioItems = async (email, categoryId, imageIndexes) => {
    const paths = imageIndexes.map(idx => `${email}/${categoryId}/${idx}`);
    await supabase.storage.from('images').remove(paths);
    const { error } = await supabase
        .from('user_portfolio')
        .delete()
        .eq('email', email)
        .eq('category_id', categoryId)
        .in('image_index', imageIndexes);
    return !error;
};

const deleteAccount = async (email) => {
    try {
        // Remove all content rows first
        await supabase.from('user_products').delete().eq('email', email);
        await supabase.from('user_offers').delete().eq('email', email);
        await supabase.from('user_portfolio').delete().eq('email', email);
        await supabase.from('user_categories').delete().eq('email', email);
        // Delete the user record last
        const { error } = await supabase.from('user').delete().eq('email', email);
        return !error;
    } catch (_) {
        return false;
    }
};

export {
    fetchCategoryCount, retrieveUserEmail, fetchBusinessInfo, fetchCategoryName,
    fetchProductInfo, fetchBusinessType, fetchBusinessPresentation, fetchProductOffer,
    fetchItemInfo, fetchCategoryIds,
    checkAccount, loginUser, registerUser, saveSecurityQuestions,
    verifySecurityQuestions, updateBusinessTypeAndCurrency, resetPassword,
    fetchSubscriptionStatus,
    updateBusinessInfo, updateBusinessPresentation, uploadAndSetLogo,
    listFolderImages, uploadItemImage,
    addProduct, updateProduct, deleteProducts,
    addCategory, renameCategory, deleteCategoryAndContents, updateCategoryCount,
    addOffer, updateOffer, deleteOffers,
    addStoreProduct, updateStoreProduct, deleteStoreProducts,
    addPortfolioItem, updatePortfolioItem, deletePortfolioItems,
    deleteAccount,
};